import { type Registry as Services, service } from '@ember/service';
import Component from '@glimmer/component';
import {
  PrismBadge,
  PrismIcon,
  PrismTooltipHeader,
} from '@mylighthouse/prism-ember';
import { t } from 'ember-intl';
import { and, gt, lte, or } from 'ember-truth-helpers';
import type { CountryCode } from 'frontend-utils/utils/countries';
import GeneralCountryFlag from 'lumen/components/general/country-flag';
import GeneralPercentageChangeValue from 'lumen/components/general/percentage-change-value';
import HotelShortTermRentalShortTermRentalEventItem from 'lumen/components/hotel/short-term-rental/short-term-rental-event-item';
import changeColorClass from 'lumen/helpers/change-color-class';
import dayjsFormat from 'lumen/helpers/dayjs-format';
import formatOtbValue from 'lumen/helpers/format-otb-value';
import hasFeature from 'lumen/helpers/has-feature';
import type {
  Event,
  Holiday,
  Occupancy,
  Period,
  ShortTermRentalOccupancy,
  ShortTermRentalRate,
} from 'lumen/models';

export type EventOrHoliday = Partial<Event> &
  Partial<Holiday> & {
    countries?: Lowercase<CountryCode>[];
    icon?: string;
  };

interface HotelRatesTooltipsShortTermRentalTooltipSignature {
  Args: {
    events?: Event[];
    hasOTBShortTermRentalAlert: boolean;
    hasRateShortTermRentalAlert: boolean;
    hasShortTermRentalAlert: boolean;
    holidays?: Holiday[];
    leadTime?: number;
    occupancy?: Occupancy;
    period: Period;
    refDaysAgo?: number;
    rentalOccupancy?: ShortTermRentalOccupancy;
    rentalRate?: ShortTermRentalRate;
    showChanges?: boolean;
    showEventsAndHolidays?: boolean;
    showLeadtime?: boolean;
    showOnlyAlert: boolean;
  };
}

export default class HotelRatesTooltipsShortTermRentalTooltip extends Component<HotelRatesTooltipsShortTermRentalTooltipSignature> {
  @service declare access: Services['access'];
  @service declare dates: Services['dates'];
  @service declare intl: Services['intl'];

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get benchmarkChange() {
    return this.args.period.getBenchmarkChange(this.args.refDaysAgo);
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get currentOccupancy() {
    return this.args.occupancy ?? this.occupancy;
  }

  get dateBetweenBrackets(): string {
    if (this.args.period.extractDate) {
      return `(${this.args.period.extractDate.format(
        this.dates.partialDateFormat.WEEKDAY_AND_DATE_SHORT,
      )})`;
    }
    return `(${this.args.period.day.date.format(
      this.dates.partialDateFormat.WEEKDAY_AND_DATE_SHORT,
    )})`;
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get eventsAndHolidays() {
    const eventsAndHolidays = [];
    if (this.args.events) {
      eventsAndHolidays.push(...this.args.events);
    }
    if (this.args.holidays) {
      eventsAndHolidays.push(...this.args.holidays);
    }
    return eventsAndHolidays as EventOrHoliday[];
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get formattedRentalOccupancyChange() {
    return this.args.period.getRentalOccupancyFormattedChange(
      this.args.refDaysAgo,
    );
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get highlightsMessage() {
    if (
      this.args.hasRateShortTermRentalAlert &&
      this.args.hasOTBShortTermRentalAlert
    ) {
      return this.intl.t('short_term_rental.tooltip.options_changed');
    }

    if (this.args.hasRateShortTermRentalAlert) {
      return this.intl.t('short_term_rental.tooltip.rates_changed');
    }

    if (this.args.hasOTBShortTermRentalAlert) {
      return this.intl.t('short_term_rental.tooltip.otb_changed');
    }

    return '';
  }

  get leadTime(): string {
    return `${this.intl.t('rates.leadtime')} ${this.args.leadTime}`;
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get occupancy() {
    return this.args.period.day?.occupancy;
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get occupancyChange() {
    return this.args.period.getOccupancyChange(this.args.refDaysAgo);
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get ownHotelRateChange() {
    return this.args.period.getOwnHotelRateChange(this.args.refDaysAgo);
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get ownHotelRateFormattedChange() {
    return this.args.period.getOwnHotelRateFormattedChange(
      this.args.refDaysAgo,
    );
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get referenceChangeDays() {
    return this.args.refDaysAgo ?? this.args.period.referenceChangeDays;
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get referenceChangeText() {
    return this.args.period.getReferenceChangeText(this.args.refDaysAgo);
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get rentalOccupancyChange() {
    return this.args.period.getRentalOccupancyChange(this.args.refDaysAgo);
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get rentalRateChange() {
    return this.args.period.getRentalRateChange(this.args.refDaysAgo);
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get rentalRateFormattedChange() {
    return this.args.period.getRentalRateFormattedChange(this.args.refDaysAgo);
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get showChangesColumn() {
    // Never show changes for past dates (same as for Rates Calendar tooltip)
    if (!this.args.period.isCurrentOrFutureDay || !this.args.showChanges) {
      return false;
    }

    return (
      this.showRateChange ||
      this.showOccupancyChange ||
      this.showMarketBenchmarkChange ||
      this.showSignificantShortTermRentalChange
    );
  }

  get showMarketBenchmarkChange(): boolean {
    return (
      this.access.hasFeature('otb.market_benchmark') &&
      this.args.period.getSignificantMarketBenchmarkChange(this.args.refDaysAgo)
    );
  }

  get showOccupancyChange(): boolean {
    return (
      this.access.hasFeature('pms.otb.view') &&
      this.args.period.getSignificantOccupancyChange(this.args.refDaysAgo)
    );
  }

  get showRateChange(): boolean {
    if (!this.ownHotelRateChange) {
      return false;
    }
    return this.ownHotelRateChange > 0;
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get showSignificantShortTermRentalChange() {
    return this.args.period.getSignificantShortTermRentalChange(
      this.args.refDaysAgo,
    );
  }

  <template>
    <PrismTooltipHeader>
      {{#if @showLeadtime}}
        {{this.leadTime}}
        {{this.dateBetweenBrackets}}
      {{else}}
        {{dayjsFormat
          @period.day.date
          format=this.dates.partialDateFormat.WEEKDAY_AND_DATE_LONG
        }}
      {{/if}}
    </PrismTooltipHeader>
    <div class="tooltip__content">
      <div class="card-summary">
        {{#if @hasShortTermRentalAlert}}
          <PrismBadge @badgeType="info">
            <PrismIcon
              @color="inherit"
              @name="short-term-rental"
              @size="extra-small"
            />
            <p class="prism-badge__sold-out-text">
              {{this.highlightsMessage}}
            </p>
          </PrismBadge>
        {{/if}}
        {{#unless @showOnlyAlert}}
          <p class="card-summary__header text-300--bold">{{t "rates.OTB"}}</p>
          <div class="card-summary__content flex justify-between">
            <p class="text-200--regular">{{t "menu.short_term_rental"}}</p>
            <div class="flex justify-end gap-100 truncate">
              <p class="metric-value">
                {{#if @rentalOccupancy}}
                  {{@rentalOccupancy.formattedValue}}
                {{else}}
                  {{@period.rentalOccupancyForCurrentDay.formattedValue}}
                {{/if}}
              </p>
              {{#if this.showChangesColumn}}
                <p
                  class="metric-value
                    {{changeColorClass this.rentalOccupancyChange}}"
                >
                  {{this.formattedRentalOccupancyChange}}
                </p>
              {{/if}}
            </div>
          </div>
          {{#if
            (or
              (hasFeature "pms.otb.view") (hasFeature "otb.empty_placeholder")
            )
          }}
            <div class="card-summary__content flex justify-between">
              <p
                class="text-200--regular
                  {{if
                    (hasFeature 'otb.empty_placeholder')
                    'text-brand font-bold'
                  }}"
              >
                {{t "rates.my_OTB"}}
              </p>
              <div class="flex justify-end gap-100 truncate">
                <p
                  class="metric-value
                    {{if
                      (hasFeature 'otb.empty_placeholder')
                      'text-brand font-bold'
                    }}"
                >
                  {{#if (hasFeature "pms.otb.view")}}
                    {{formatOtbValue
                      this.currentOccupancy.occupancyPercentage
                      style="percent"
                    }}
                  {{else if
                    (or
                      (hasFeature "otb.empty_placeholder")
                      (hasFeature "otb.data_validation")
                    )
                  }}
                    {{t "general.missing"}}
                  {{/if}}
                </p>
                {{#if this.showChangesColumn}}
                  <p class="metric-value">
                    {{#if this.showOccupancyChange}}
                      <GeneralPercentageChangeValue
                        @changeValue={{this.occupancyChange}}
                      />
                    {{/if}}
                  </p>
                {{/if}}
              </div>
            </div>

            {{#if (hasFeature "otb.market_benchmark")}}
              <div class="card-summary__content flex justify-between gap-100">
                <p class="text-200--regular">
                  {{t "rates.tooltip.market_otb"}}
                </p>
                {{#if
                  (and
                    @period.isCurrentOrFutureDay
                    this.occupancy.isMarketOtbOutOfSync
                  )
                }}
                  <PrismIcon @name="sync-disabled" @size="extra-small" />
                {{/if}}
                <div class="flex justify-end gap-100 truncate">
                  <p class="metric-value">
                    {{formatOtbValue
                      this.currentOccupancy.benchmarkValueNormalized
                      style="percent"
                    }}
                  </p>
                  {{#if this.showChangesColumn}}
                    <p class="metric-value">
                      {{#if this.showMarketBenchmarkChange}}
                        <GeneralPercentageChangeValue
                          @changeValue={{this.benchmarkChange}}
                        />
                      {{else}}
                        {{t "general.missing"}}
                      {{/if}}
                    </p>
                  {{/if}}
                </div>
              </div>
            {{/if}}
          {{/if}}

          <p class="card-summary__header text-300--bold">
            {{t "general.rate"}}
          </p>
          <div class="card-summary__content flex justify-between">
            <p class="text-200--regular">{{t "menu.short_term_rental"}}</p>
            <div class="flex justify-end gap-100 truncate">
              <p class="metric-value">
                {{#if @rentalRate}}
                  {{@rentalRate.formattedValue}}
                {{else}}
                  {{@period.rentalRateForCurrentDay.formattedValue}}
                {{/if}}
              </p>
              {{#if this.showChangesColumn}}
                <p
                  class="metric-value
                    {{changeColorClass this.rentalRateChange}}"
                >
                  {{this.rentalRateFormattedChange}}
                </p>
              {{/if}}
            </div>
          </div>
          <div class="card-summary__content flex justify-between">
            <p class="text-200--regular">
              {{t "general.my_hotel"}}
            </p>
            <div class="flex justify-end gap-100 truncate">
              <p class="metric-value">
                {{@period.formattedOwnHotelRate}}
              </p>
              {{#if this.showChangesColumn}}
                <p
                  class="metric-value
                    {{changeColorClass this.ownHotelRateChange}}"
                >
                  {{this.ownHotelRateFormattedChange}}
                </p>
              {{/if}}
            </div>
          </div>
          {{#if (and @showChanges @period.isCurrentOrFutureDay)}}
            <div class="label mt-500">
              {{#if this.showSignificantShortTermRentalChange}}
                {{t "reports.change"}}
                {{t "general.daysago" count=this.referenceChangeDays}}
              {{else}}
                {{this.referenceChangeText}}
              {{/if}}
            </div>
          {{/if}}
          {{#if
            (and @showEventsAndHolidays (gt this.eventsAndHolidays.length 0))
          }}
            <p class="card-summary__header text-300--bold">
              {{t "market_insight.detail_pane.tabs.events_holidays"}}
            </p>
            <div class="card-summary__events">
              {{#each this.eventsAndHolidays as |event|}}
                {{#if (lte event.countries.length 2)}}
                  {{#each event.countries as |country|}}
                    <div class="card-summary__content flex flex-row">
                      <div class="mr-200">
                        <GeneralCountryFlag @countryCode={{country}} />
                      </div>
                      <HotelShortTermRentalShortTermRentalEventItem
                        @event={{event}}
                        @partialDateFormat={{this.dates.partialDateFormat}}
                      />
                    </div>
                  {{/each}}
                {{else}}
                  <div class="card-summary__content flex flex-row">
                    {{#if event.countries}}
                      <div class="mr-200">
                        <GeneralCountryFlag @countryCode="multiple" />
                      </div>
                    {{else if event.icon}}
                      <PrismIcon
                        @name={{event.icon}}
                        @size="small"
                        class="mr-200"
                      />
                    {{/if}}
                    <HotelShortTermRentalShortTermRentalEventItem
                      @event={{event}}
                      @partialDateFormat={{this.dates.partialDateFormat}}
                    />
                  </div>
                {{/if}}
              {{/each}}
            </div>
          {{/if}}
        {{/unless}}
      </div>
    </div>
  </template>
}
